/**
 * TelemetryProvider - Wraps the app to provide page view tracking and error boundary.
 *
 * Features:
 * - Tracks page_view events on route changes
 * - Catches and reports frontend errors via error boundary
 * - Flushes telemetry on page unload
 */
import React, { useEffect, Component } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackError, flushTelemetry } from '../utils/telemetry';

/**
 * PageViewTracker - Tracks page views on route changes.
 * Must be inside a Router context.
 */
export function PageViewTracker({ children }) {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    // Extract a reasonable page title from the pathname
    const pathname = location.pathname;
    let pageTitle = 'Home';

    if (pathname === '/') {
      pageTitle = 'Home';
    } else if (pathname.startsWith('/frogs/products/')) {
      pageTitle = 'Product Detail';
    } else if (pathname.startsWith('/frogs/')) {
      pageTitle = 'Frog Genus';
    } else if (pathname === '/frogs') {
      pageTitle = 'Frogs';
    } else if (pathname === '/cart') {
      pageTitle = 'Cart';
    } else if (pathname === '/checkout/address') {
      pageTitle = 'Checkout - Address';
    } else if (pathname === '/checkout/payment') {
      pageTitle = 'Checkout - Payment';
    } else if (pathname.startsWith('/order-confirmation/')) {
      pageTitle = 'Order Confirmation';
    } else if (pathname.startsWith('/account/orders/')) {
      pageTitle = 'Order Detail';
    } else if (pathname === '/account/orders') {
      pageTitle = 'My Orders';
    } else if (pathname === '/account/froggy-chat') {
      pageTitle = 'FroggyAI Chat';
    } else if (pathname.startsWith('/care-guides/')) {
      pageTitle = 'Care Guide';
    } else if (pathname === '/care-guides') {
      pageTitle = 'Care Guides';
    } else if (pathname === '/about') {
      pageTitle = 'About Us';
    } else if (pathname === '/faq') {
      pageTitle = 'FAQ';
    } else if (pathname === '/shipping-policy') {
      pageTitle = 'Shipping Policy';
    } else if (pathname === '/privacy-policy') {
      pageTitle = 'Privacy Policy';
    } else if (pathname === '/terms') {
      pageTitle = 'Terms of Service';
    } else if (pathname === '/login') {
      pageTitle = 'Login';
    } else if (pathname === '/signup') {
      pageTitle = 'Sign Up';
    } else if (pathname.startsWith('/promo/')) {
      pageTitle = 'Promo Landing';
    } else if (pathname.startsWith('/facilities/')) {
      pageTitle = 'Facility Management';
    } else if (pathname.startsWith('/my-store/')) {
      pageTitle = 'My Store';
    } else {
      // Extract last segment as title
      const segments = pathname.split('/').filter(Boolean);
      if (segments.length > 0) {
        pageTitle = segments[segments.length - 1]
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }

    trackPageView(pageTitle, {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location]);

  return children;
}

/**
 * TelemetryErrorBoundary - Catches and reports React errors.
 */
export class TelemetryErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Track the error
    trackError(error, {
      component: 'ErrorBoundary',
      componentStack: errorInfo?.componentStack?.substring(0, 1000) || null,
      source: 'react_error_boundary',
    });

    // Also flush immediately to ensure the error is captured
    flushTelemetry();

    // Log to console for debugging
    console.error('[TelemetryErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          minHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <h1>Something went wrong</h1>
          <p style={{ color: '#666', marginTop: '10px' }}>
            We've been notified of this error and are working to fix it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export default combined provider
export default function TelemetryProvider({ children }) {
  return (
    <TelemetryErrorBoundary>
      <PageViewTracker>
        {children}
      </PageViewTracker>
    </TelemetryErrorBoundary>
  );
}
